import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import BrandName from './BrandName';

interface NavigationItem {
  name: string;
  icon: React.ComponentType<any>;
  path?: string;
  description: string;
  children?: NavigationItem[];
  expanded?: boolean;
}

interface SidebarProps {
  items: NavigationItem[];
  bottomItems?: NavigationItem[];
  collapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ items, bottomItems = [], collapsed, onToggle }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Auto-expand a section when the current route matches a child (or /context overview for Test Mapping)
  useEffect(() => {
    const pathname = location.pathname;
    setExpandedItems((prev) => {
      const next = new Set(prev);
      const consider = [...items, ...bottomItems];
      consider.forEach((item) => {
        if (!item.name || !item.children?.length) return;
        const hasActiveChild = item.children.some((c) => {
          if (!c.path) return false;
          return pathname === c.path || pathname.startsWith(`${c.path}/`);
        });
        const hasContextChild = item.children.some((c) => c.path?.startsWith('/context'));
        const onContextRoute = pathname === '/context' || pathname.startsWith('/context/');
        if (hasActiveChild || (hasContextChild && onContextRoute)) {
          next.add(item.name);
        }
      });
      return next;
    });
  }, [location.pathname, items, bottomItems]);

  const toggleExpanded = (itemName: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.name);
    const isActive = item.path ? location.pathname === item.path : false;
    const hasActiveChild = item.children?.some(child => 
      child.path ? location.pathname === child.path : false
    );

    const baseClasses = `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
      level > 0 ? 'ml-4' : ''
    }`;
    
    const activeClasses = isActive || hasActiveChild
      ? 'bg-blue-600 text-white border-r-2 border-blue-500'
      : 'text-slate-300 hover:bg-slate-800 hover:text-slate-200';

    return (
      <div key={item.name}>
        {hasChildren ? (
          <button
            onClick={() => !collapsed && toggleExpanded(item.name)}
            className={`${baseClasses} ${activeClasses} w-full text-left`}
            title={collapsed ? item.description : undefined}
          >
            <Icon
              className={`mr-3 flex-shrink-0 h-5 w-5 ${
                isActive || hasActiveChild ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'
              }`}
            />
            {!collapsed && (
              <>
                <span className="truncate flex-1">{item.name}</span>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </>
            )}
          </button>
        ) : (
          <NavLink
            to={item.path!}
            className={`${baseClasses} ${activeClasses}`}
            title={collapsed ? item.description : undefined}
          >
            <Icon
              className={`mr-3 flex-shrink-0 h-5 w-5 ${
                isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'
              }`}
            />
            {!collapsed && (
              <span className="truncate">{item.name}</span>
            )}
          </NavLink>
        )}
        
        {hasChildren && isExpanded && !collapsed && (
          <div className="ml-2 mt-1 space-y-1">
            {item.children!.map(child => renderNavigationItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`bg-slate-900 shadow-lg transition-all duration-300 ease-in-out flex flex-col h-screen border-r border-slate-700 ${
      collapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700 flex-shrink-0">
        {!collapsed && (
          <h1 className="text-xl font-bold">
            <BrandName size="md" />
          </h1>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-6 flex-1 overflow-y-auto">
        <div className="px-2 space-y-1">
          {items.map((item) => renderNavigationItem(item))}
        </div>
      </nav>

      {/* Settings section at bottom */}
      {bottomItems.length > 0 && (
        <div className="px-2 py-2 border-t border-slate-700 flex-shrink-0">
          <div className="space-y-1">
            {bottomItems.map((item) => renderNavigationItem(item))}
          </div>
        </div>
      )}

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          <div className="text-xs text-slate-500 text-center">
            <p>
              <BrandName size="sm" className="inline" /> v1.0.0
            </p>
            <p className="mt-1">AI-Powered Testing</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
